// Vibe Check (contract D.4.2, B.11): a typed box above the Set Search
// builder. The answer's FindQuery is merged into the draft with
// queryOps.mergeVibe (chips are a union; filters the answer sets replace the
// draft's), then handed up with onApply(merged, { run: false }). Words that
// matched nothing show as grey "not found" chips. When the helper service is
// resting, off or failing, the parser result still applies and a quiet line
// says the filters still work. When an answer applied something, a compact
// "Got it:" line names what was added (so it is clear the ask worked); when it
// added nothing, the note suggests two or three phrasings this band's vibes
// understand, each one click to try.
//
// Props: { band, meta, value, onApply(nextQuery, info) }
// Optional (additive): `catalog` ({ songs }) for the example in the placeholder.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../api';
import { mergeVibe, isEmpty, SLOT_LABELS } from '../search/queryOps';
import { IconSparkle, IconInfo } from '../icons';
import { resolveBand, useUid } from './vibeUtil';
import './vibe.css';

const MAX_LEN = 200;
const RESTING = 'Vibe Check is resting right now. The filters below still work.';
const ASK = 'Describe what you want to hear';

// The example comes from this band's own data: one of its eras, "soundboard"
// only where the band has soundboard tapes, and one of its three most played
// songs. `pickA` / `pickB` in [0, 1) choose the era and the song. Segments like
// Drums or Space top the Dead's play counts but read oddly as "a long Drums",
// so they are skipped. It is shown under the field as a clickable "Try" line
// rather than in the placeholder, where it would be cut off.
const SEGMENT_NAME = /^(drums|space|jam|drums\s*[/&>-]\s*space|rhythm devils|tuning|intro|crowd)$/i;

export const buildExample = (meta, songs, pickA = 0, pickB = 0) => {
  const eras = meta && Array.isArray(meta.eras) ? meta.eras.filter((e) => e && typeof e.label === 'string' && e.label) : [];
  const sbd = !!(meta && meta.features && meta.features.sbd);
  const top = (Array.isArray(songs) ? songs : [])
    .filter((s) => s && typeof s.name === 'string' && s.name && Number.isFinite(Number(s.plays)))
    .filter((s) => !SEGMENT_NAME.test(s.name.trim()))
    .slice()
    .sort((a, b) => Number(b.plays) - Number(a.plays))
    .slice(0, 3);
  const era = eras.length ? eras[Math.min(eras.length - 1, Math.floor(pickA * eras.length))].label : null;
  const song = top.length ? top[Math.min(top.length - 1, Math.floor(pickB * top.length))].name : null;
  const head = [era, sbd ? 'soundboard' : era ? 'shows' : null].filter(Boolean).join(' ');
  const tail = song ? (head ? 'with a long ' : 'a long ') + song : '';
  const example = [head, tail].filter(Boolean).join(' ');
  return example || null;
};

// kept for callers that want the one-line form
export const buildPlaceholder = (meta, songs, pickA = 0, pickB = 0) => {
  const example = buildExample(meta, songs, pickA, pickB);
  return example ? ASK + ', like: ' + example : ASK;
};

const emptyResult = { notFound: [], explain: [], banner: null, nothing: false, got: [] };

const SORT_WORDS = { rating: 'Top rated', downloads: 'Most downloaded', jams: 'Longest jams', rare: 'Rarest', '-date': 'Newest first' };

// What an answer applied, as short labels in reading order ("Exploratory", "1965-69",
// "Morning Dew (long)", "Soundboard"). Built from the answer itself, so exact matches
// (which the server does not explain) are listed too.
export const appliedLabels = (answer, meta) => {
  const q = answer || {};
  const out = [];
  const songs = Array.isArray(q.songs) ? q.songs : [];
  const links = Array.isArray(q.links) ? q.links : [];
  let run = '';
  songs.forEach((s, i) => {
    if (!s || typeof s.name !== 'string') return;
    let t = s.name;
    if (s.jam === 90) t += ' (long)';
    else if (s.jam === 75) t += ' (stretched)';
    if (s.slot && s.slot !== 'any' && SLOT_LABELS[s.slot]) t += ', ' + SLOT_LABELS[s.slot].toLowerCase();
    if (i > 0 && links[i - 1]) run += ' > ' + t;
    else {
      if (run) out.push(run);
      run = t;
    }
  });
  if (run) out.push(run);
  (Array.isArray(q.sandwiches) ? q.sandwiches : []).forEach((w) => {
    if (w && w.outer && w.inner) out.push(w.outer.name + ' > ' + w.inner.name + ' > ' + w.outer.name);
  });
  (Array.isArray(q.not) ? q.not : []).forEach((x) => x && x.name && out.push('no ' + x.name));
  if (q.venue && q.venue.name) out.push(q.venue.name);
  if (q.city && q.city.label) out.push(q.city.label);
  const eras = meta && Array.isArray(meta.eras) ? meta.eras : [];
  const era = q.era ? eras.find((e) => e && e.key === q.era) : null;
  if (era && era.label) out.push(era.label);
  else if (Array.isArray(q.years) && q.years.length === 2) out.push(q.years[0] === q.years[1] ? String(q.years[0]) : q.years[0] + '-' + q.years[1]);
  if (q.sbd) out.push('Soundboard');
  if (q.vibe) {
    const vs = meta && meta.features && Array.isArray(meta.features.vibes) ? meta.features.vibes : [];
    const v = vs.find((x) => x && (x.key === q.vibe || x === q.vibe));
    out.push((v && v.label) || q.vibe);
  }
  if (q.sort && SORT_WORDS[q.sort]) out.push(SORT_WORDS[q.sort]);
  if (q.mode === 'min' && songs.length > 1) out.push('any ' + (q.minN || 1) + ' of these songs');
  return out;
};

// Two or three phrasings this band understands, from its own vibes (and eras).
const VIBE_TRIES = [
  ['exploratory', 'heady jams'],
  ['ballads', 'mellow ballads'],
  ['rockers', 'high energy rockers'],
  ['cowboy', 'cowboy songs'],
  ['bluegrass', 'bluegrass picking'],
  ['stretched', 'long jams'],
  ['deep-cuts', 'deep cuts'],
  ['crowd', 'the classics'],
];
export const suggestionsFor = (meta) => {
  const vs = meta && meta.features && Array.isArray(meta.features.vibes) ? meta.features.vibes : [];
  const keys = new Set(vs.map((v) => (v && typeof v === 'object' ? v.key : v)));
  const out = VIBE_TRIES.filter(([k]) => keys.has(k)).slice(0, 2).map(([, t]) => t);
  const hasEras = meta && ((Array.isArray(meta.eras) && meta.eras.length) || (Array.isArray(meta.years) && meta.years.length === 2));
  if (hasEras) out.push(out.length ? 'early stuff' : 'late era');
  return out.length ? out : ['long jams', 'early stuff'];
};

// true when the answer carries no chip and no filter at all
const answerIsBlank = (q) => !q || (isEmpty(q) && (!q.mode || q.mode === 'all') && (!q.sort || q.sort === 'date'));

export default function VibeCheckBox({ band, meta, value, onApply, catalog }) {
  const b = resolveBand(band);
  const bandKey = b ? b.key : null;
  const songs = catalog && Array.isArray(catalog.songs) ? catalog.songs : null;
  // two stable random picks per band, so the example does not change while typing
  // (bandKey is the deliberate trigger: new band, new example)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const picks = useMemo(() => [Math.random(), Math.random()], [bandKey]);
  const example = useMemo(() => buildExample(meta, songs, picks[0], picks[1]), [meta, songs, picks]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(emptyResult);
  const reqRef = useRef(0);
  const valueRef = useRef(value);
  valueRef.current = value;
  const inputId = useUid('vb-vc');
  const inputRef = useRef(null);
  const statusId = useUid('vb-vc-status');

  useEffect(() => {
    reqRef.current += 1;
    setText('');
    setLoading(false);
    setRes(emptyResult);
  }, [bandKey]);

  if (!b || (meta && meta.mode === 'off')) return null;

  const submit = (e) => {
    if (e) e.preventDefault();
    if (loading) return;
    const q = text.trim();
    if (!q) {
      setRes({ ...emptyResult, banner: 'Describe what you want to hear first.' });
      return;
    }
    const my = ++reqRef.current;
    setLoading(true);
    api
      .vibeAsk(b, q)
      .then((r) => {
        if (my !== reqRef.current) return;
        const answer = r && r.query;
        const blank = answerIsBlank(answer);
        if (!blank) onApply(mergeVibe(valueRef.current, answer), { run: false });
        const ai = r && r.ai;
        setRes({
          notFound: (Array.isArray(r && r.notFound) ? r.notFound : []).filter((x) => x && typeof x.text === 'string' && x.text.trim()),
          explain: (Array.isArray(r && r.explain) ? r.explain : []).filter((x) => typeof x === 'string' && x),
          banner: ai === 'resting' || ai === 'off' || ai === 'error' ? RESTING : null,
          nothing: blank,
          got: blank ? [] : appliedLabels(answer, meta),
        });
      })
      .catch((err) => {
        if (my !== reqRef.current) return;
        setRes({ ...emptyResult, banner: (err && err.userMessage) || RESTING });
      })
      .finally(() => {
        if (my === reqRef.current) setLoading(false);
      });
  };

  const bandName = b.name;
  const tries = res.nothing ? suggestionsFor(meta) : [];
  const tryText = (t) => {
    setText(t);
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className="vb-vc vb-root">
      <label className="vb-label" htmlFor={inputId}>
        <IconSparkle size={12} /> Vibe Check
      </label>
      <form className="vb-vc-form" onSubmit={submit} role="search">
        <input
          id={inputId}
          ref={inputRef}
          className="vb-vc-input"
          type="text"
          value={text}
          maxLength={MAX_LEN}
          placeholder={ASK}
          autoComplete="off"
          spellCheck="false"
          aria-describedby={statusId}
          onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
        />
        <button type="submit" className="vb-vc-go" title="Vibe Check" aria-label="Run Vibe Check" disabled={loading || !text.trim()}>
          {loading ? <span className="spinner vb-spin" aria-hidden="true" /> : <IconSparkle size={15} />}
        </button>
      </form>
      {example && !text && (
        <div className="vb-vc-try">
          <span className="vb-vc-try-label">Try</span>
          <button
            type="button"
            className="vb-vc-example"
            onClick={() => tryText(example)}
          >
            {example}
          </button>
        </div>
      )}

      <div id={statusId} aria-live="polite">
        {res.got.length > 0 && (
          <div className="vb-note vb-vc-got" style={{ marginTop: 8, opacity: 1 }}>
            <strong>Got it:</strong> {res.got.join(', ')}
          </div>
        )}
        {res.notFound.length > 0 && (
          <ul className="vb-nf-row" aria-label="Words not found">
            <li className="vb-nf-label">Not found:</li>
            {res.notFound.map((x, i) => (
              <li key={x.text + ':' + i} className="vb-nf" title={'Not in the ' + bandName + ' catalog'}>
                {x.text}
              </li>
            ))}
          </ul>
        )}
        {res.explain.length > 0 && (
          <ul className="vb-explain">
            {res.explain.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        )}
        {res.nothing && !res.banner && (
          <div className="vb-vc-try" style={{ flexWrap: 'wrap', rowGap: 2 }}>
            <span className="vb-vc-try-label">Nothing to add from that. Try</span>
            {tries.map((t, i) => (
              <React.Fragment key={t}>
                {i > 0 && <span className="vb-vc-try-label">or</span>}
                <button type="button" className="vb-vc-example" style={{ whiteSpace: 'nowrap' }} onClick={() => tryText(t)}>
                  {t}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
        {res.banner && (
          <div className="vb-banner">
            <IconInfo size={14} />
            <span>{res.banner}</span>
          </div>
        )}
      </div>
    </div>
  );
}
